import { Head, Link, router } from '@inertiajs/react'
import { Plus, Search, X } from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'

import { PageHeader } from '@/components/PageHeader'
import { Pagination } from '@/components/Pagination'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { DashboardLayout } from '@/layouts/DashboardLayout'
import { batchesPath, editBatchPath, newBatchPath } from '@/lib/routes'
import { useDebouncedCallback } from '@/lib/useDebouncedCallback'
import type { PaginationMeta } from '@/types/inertia'

type BatchRow = {
  id: number
  code: string
  year: number
  period: 1 | 2
  company_count: number
}

type BatchesIndexProps = {
  batches: BatchRow[]
  pagination: PaginationMeta
  filters: { q: string | null }
}

const PARTIAL_RELOAD_KEYS = ['batches', 'pagination', 'filters']

function visitWith(q: string | null) {
  router.get(
    batchesPath(),
    q ? { q } : {},
    {
      preserveState: true,
      preserveScroll: true,
      replace: true,
      only: PARTIAL_RELOAD_KEYS,
    },
  )
}

function BatchesIndex({ batches, pagination, filters }: BatchesIndexProps) {
  const [query, setQuery] = useState(filters.q ?? '')

  useEffect(() => {
    setQuery(filters.q ?? '')
  }, [filters.q])

  const searchDebounced = useDebouncedCallback((next: string) => {
    visitWith(next || null)
  }, 300)

  const onChange = (value: string) => {
    setQuery(value)
    searchDebounced(value)
  }

  const clear = () => {
    setQuery('')
    searchDebounced.cancel()
    visitWith(null)
  }

  return (
    <>
      <Head title="Batches" />

      <div className="mx-auto max-w-4xl px-8 py-10">
        <PageHeader
          title="Batches"
          description="The generational cohorts portfolio companies belong to."
          actions={
            <Link href={newBatchPath()}>
              <Button>
                <Plus />
                New batch
              </Button>
            </Link>
          }
        />

        <div className="relative mb-4">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="h-12 pl-11 pr-12 text-base"
            placeholder="Search batches by code or year…"
            value={query}
            onChange={(e) => onChange(e.target.value)}
          />
          {query && (
            <button
              type="button"
              onClick={clear}
              aria-label="Clear search"
              className="absolute right-3 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="mb-3 flex items-center justify-end text-xs text-muted-foreground">
          <span className="tabular-nums">{pagination.total_count} total</span>
        </div>

        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="border-b border-border text-left text-xs uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="px-6 py-3 font-medium">Code</th>
                  <th className="px-6 py-3 font-medium">Year</th>
                  <th className="px-6 py-3 font-medium">Period</th>
                  <th className="px-6 py-3 font-medium text-right">Companies</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {batches.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-6 py-10 text-center text-muted-foreground">
                      {query ? 'No batches match that search.' : 'No batches yet.'}
                    </td>
                  </tr>
                )}
                {batches.map((b) => (
                  <tr
                    key={b.id}
                    className="cursor-pointer transition-colors hover:bg-muted/50"
                    onClick={() => router.visit(editBatchPath(b.id))}
                  >
                    <td className="px-6 py-4 font-medium">{b.code}</td>
                    <td className="px-6 py-4 tabular-nums text-muted-foreground">{b.year}</td>
                    <td className="px-6 py-4 text-muted-foreground">
                      {b.period === 1 ? 'I' : 'II'}
                    </td>
                    <td className="px-6 py-4 text-right tabular-nums text-muted-foreground">
                      {b.company_count}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <div className="mt-4">
          <Pagination
            basePath={batchesPath()}
            currentPage={pagination.current_page}
            nextPage={pagination.next_page}
            prevPage={pagination.prev_page}
            totalPages={pagination.total_pages}
            query={query ? { q: query } : {}}
          />
        </div>
      </div>
    </>
  )
}

export default Object.assign(BatchesIndex, {
  layout: (page: ReactNode) => <DashboardLayout>{page}</DashboardLayout>,
})
